import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell, { PublicAuthedBanner } from "@/components/AppShell";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Features from "@/pages/Features";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import SonographerDashboard from "@/pages/SonographerDashboard";
import SonographerSchedule from "@/pages/SonographerSchedule";
import SonographerStatistics from "@/pages/SonographerStatistics";
import SonographerAccount from "@/pages/SonographerAccount";
import VerifyEmail from "@/pages/VerifyEmail";
import VideosPage from "@/pages/VideosPage";
import WorldRecords from "@/pages/WorldRecords";
import ManagerDashboard from "@/pages/ManagerDashboard";
import ManagerStatistics from "@/pages/ManagerStatistics";
import ManagerSonographers from "@/pages/ManagerSonographers";
import ManagerReports from "@/pages/ManagerReports";
import ManagerStretching from "@/pages/ManagerStretching";
import AdminDashboard from "@/pages/AdminDashboard";
import ClinicBadge from "@/pages/ClinicBadge";
import BillingSuccess from "@/pages/BillingSuccess";

function Authed({ children, roles }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function AppShellRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const isAuthArea = location.pathname.startsWith("/app") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/badge/") ||
    location.pathname === "/verify-email" ||
    location.pathname === "/login";

  return (
    <>
      {!isAuthArea && <Navbar />}
      {!isAuthArea && <PublicAuthedBanner />}
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<Features />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Public clinic badge */}
        <Route path="/badge/:clinicId" element={<ClinicBadge />} />
        <Route path="/billing/success" element={<BillingSuccess />} />

        {/* Sonographer */}
        <Route path="/app/dashboard" element={<Authed roles={["sonographer"]}><SonographerDashboard /></Authed>} />
        <Route path="/app/statistics" element={<Authed roles={["sonographer"]}><SonographerStatistics /></Authed>} />
        <Route path="/app/schedule" element={<Authed roles={["sonographer"]}><SonographerSchedule /></Authed>} />
        <Route path="/app/account" element={<Authed roles={["sonographer"]}><SonographerAccount /></Authed>} />
        <Route path="/app/videos" element={<Authed roles={["sonographer"]}><VideosPage /></Authed>} />

        {/* Manager */}
        <Route path="/app/clinic" element={<Authed roles={["clinic_manager"]}><ManagerDashboard /></Authed>} />
        <Route path="/app/clinic/statistics" element={<Authed roles={["clinic_manager"]}><ManagerStatistics /></Authed>} />
        <Route path="/app/clinic/sonographers" element={<Authed roles={["clinic_manager"]}><ManagerSonographers /></Authed>} />
        <Route path="/app/clinic/stretching" element={<Authed roles={["clinic_manager"]}><ManagerStretching /></Authed>} />
        <Route path="/app/clinic/reports" element={<Authed roles={["clinic_manager"]}><ManagerReports /></Authed>} />

        {/* Shared */}
        <Route path="/app/records" element={<Authed roles={["sonographer", "clinic_manager"]}><WorldRecords /></Authed>} />

        {/* Admin */}
        <Route path="/admin" element={<Authed roles={["admin"]}><AdminDashboard /></Authed>} />

        {/* Legacy redirects */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/manager" element={<Navigate to="/app/clinic" replace />} />
      </Routes>
      {!isAuthArea && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShellRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
